import AuthService from "@core/services/auth/authService";
import defaultConfig from "./dealershipsDefaultConfig";
import { AxiosInstance, AxiosResponse } from "axios";
import {
  IDearlershipsService,
  TDealershipsConfig,
  IResponseGetDealership,
  IResponseListDealerships,
  IResponseGetFiltersDealerships,
  IResponseCreateDealership,
  IUpdateDealershipBody,
  ICreateDealershipBody,
  IFieldControl,
  IResponseValidCnpj,
  IDealerships,
} from "@core/services/interfaces/business/dealerships/IDealershipsService";
import { IBodyDealerships } from "@core/services/interfaces/business/dealerships/IBodySearch";

export default class DearlershipsService
  extends AuthService
  implements IDearlershipsService
{
  serviceDealershipsConfig: TDealershipsConfig;

  constructor(axiosIns: AxiosInstance, overrideConfig: Object) {
    super(axiosIns, overrideConfig);
    this.serviceDealershipsConfig = { ...defaultConfig, ...overrideConfig };
  }

  /**
   * Retorna uma lista concessionárias por Id
   */
  requestGet(dealershipsId: string): Promise<IResponseGetDealership> {
    return this.axiosIns.get(
      this.serviceDealershipsConfig.getDealershipByIdEndpoint +
        `?id=${dealershipsId}`
    );
  }

  requestUpdate(
    body: IUpdateDealershipBody
  ): Promise<IResponseCreateDealership> {
    return this.axiosIns.put(
      this.serviceDealershipsConfig.updateDealershipEndpoint,
      body
    );
  }

  requestCreate(
    body: ICreateDealershipBody
  ): Promise<IResponseCreateDealership> {
    return this.axiosIns.post(
      this.serviceDealershipsConfig.createDealershipEndpoint,
      body
    );
  }

  /**
   * Retorna uma lista concessionárias por Id
   */
  requestGetFilters(): Promise<IResponseGetFiltersDealerships> {
    return this.axiosIns.get(
      this.serviceDealershipsConfig.getFiltersDealershipsIdEndpoint
    );
  }

  /**
   * Retorna uma lista de concessionárias
   */
  requestList(args: IBodyDealerships): Promise<IResponseListDealerships> {
    return this.axiosIns.post(
      this.serviceDealershipsConfig.getListDealershipsEndpoint +
        `?paginar=${args.paginate}`,
      args
    );
  }

  /**
   * Busca via endpoint /ListarEmpresas (mais leve, SQL-translatável).
   * Retorna o primeiro registro cujo Cnpj bate exato, ou null.
   * Em caso de erro de rede/lambda, retorna null para não bloquear o cadastro.
   */
  private findEmpresaByCnpj(cnpj: string): Promise<any | null> {
    return this.axiosIns
      .post(
        "/api/GerenciarEmpresas/EmpresaGrid/ListarEmpresas?paginar=true",
        {
          draw: 1,
          search: { value: cnpj, regex: false },
          length: 5,
          start: 0,
        }
      )
      .then((response: AxiosResponse<any>): any | null => {
        const items: any[] = response.data?.data ?? [];
        return items.find((it) => it?.Cnpj == cnpj) ?? null;
      })
      .catch(() => null);
  }

  /**
   * True - válido (CNPJ disponível)
   * False - inválido (já cadastrado)
   * Null - ambíguo (erro de rede); chamador deve assumir válido para não bloquear.
   */
  requestValidCnpj(
    cnpj: string,
    checkHimself = false
  ): Promise<boolean | null> {
    return this.findEmpresaByCnpj(cnpj).then((empresa) => {
      if (empresa === null) {
        // Pode ser inexistente ou erro. Sem evidência de duplicidade, permite seguir.
        return true;
      }
      if (checkHimself) {
        return empresa.Cnpj == cnpj;
      }
      return false;
    });
  }

  /**
   * Busca a empresa existente por CNPJ (usado para oferecer edição ao detectar duplicado no cadastro).
   * Retorna null se não encontrar ou em caso de erro.
   */
  requestFindByCnpj(cnpj: string): Promise<IDealerships | null> {
    return this.findEmpresaByCnpj(cnpj).then((empresa): IDealerships | null => {
      if (!empresa) return null;
      return {
        Id: empresa.Id,
        CNPJ: empresa.Cnpj,
        RazaoSocial: empresa.RazaoSocial ?? "",
        NomeFantasia: empresa.RazaoSocial ?? "",
      } as IDealerships;
    });
  }

  getFieldControl(): IFieldControl {
    const fieldControl = require(`@core/services/business/dealerships/fieldControl/field.${this.getDomain()}`);
    return fieldControl.default;
  }
}
